import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';

import FormInput from '../../../components/form/FormInput';

import { brandingAdd, brandingUpdate } from '../../../store/slices/adminSlices/brandingSlice';

const AddEditBrandingModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const initialValues = {
    company_domain: '',
    header_text: '',
    footer_text: '',
    header: '',
    favicon: ''
  };

  const validationSchema = Yup.object({
    company_domain: Yup.string().required('zone name is required'),
    header_text: Yup.string().required('header text is required'),
    footer_text: Yup.string().required('footer text is required')
    // header: Yup.string().required('header is required'),
    // favicon: Yup.string().required('favicon is required')
  });

  const onSubmit = async (values) => {
    if (!values?.header) {
      setFieldError('header', 'Header image is required');
      return false;
    }
    if (!values?.favicon) {
      setFieldError('favicon', 'Favicon icon is required');
      return false;
    }

    const formData = new FormData();
    for (const key in values) {
      if (values[key] !== undefined && values[key] !== null) {
        formData.append(key, values[key]);
      }
    }

    try {
      const response = isEdit
        ? await dispatch(brandingUpdate({ id: editRecord.id, formData: formData })).unwrap()
        : await dispatch(brandingAdd(formData)).unwrap();

      if (response && response?.message) {
        toast.success(`Record ${isEdit ? 'Updated' : 'Added'} Successfully`);
        onCloseClick(true);
        resetForm();
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      console.log('error', error);
      toast.error('Something went wrong.');
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue,
    setFieldError
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    const urlToFile = async (url) => {
      try {
        const fileName = url.substring(url.lastIndexOf('/') + 1);
        const extension = fileName.split('.').pop().toLowerCase();
        const mimeType = extension === 'svg' ? 'image/svg+xml' : `image/${extension}`;
        const response = await fetch(url);
        const blob = await response.blob();
        return new File([blob], fileName, { type: mimeType });
      } catch (error) {
        console.error('Error converting URL to File:', error);
        return null;
      }
    };

    const loadFiles = async () => {
      if (isEdit && editRecord) {
        const headerFile = editRecord?.headerlogo_path
          ? await urlToFile(editRecord.headerlogo_path)
          : null;
        const faviconFile = editRecord?.favicon_path
          ? await urlToFile(editRecord.favicon_path)
          : null;
        setValues({
          ...editRecord,
          header_text: editRecord?.header_text,
          company_domain: editRecord?.company_domain,
          footer_text: editRecord?.footer_text,
          header: headerFile,
          favicon: faviconFile
        });
      }
    };

    loadFiles();
  }, [isEdit, editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Timezone' : 'Add Timezone'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="timezoneForm" onSubmit={handleSubmit}>
          <Row>
            <Col lg="4">
              <FormInput
                name="company_domain"
                label="Company Domain"
                placeholder="Enter company domain"
                value={values.company_domain}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="header_text"
                label="Header text"
                placeholder="Enter header text"
                value={values.header_text}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="footer_text"
                label="Footer text"
                placeholder="Enter footer text"
                value={values.footer_text}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="4">
              <FormGroup>
                <Label htmlFor="header-file-upload">Header Icon</Label>
                <div className="file-upload-container cursor-pointer">
                  <Input
                    type="text"
                    className={`file-name cursor-pointer ${errors['header'] ? 'error-input' : ''}`}
                    placeholder={
                      values.header && values.header.name
                        ? values.header.name
                        : 'Choose an SVG file'
                    }
                    readOnly
                  />
                  <label className="upload-button cursor-pointer">
                    <Icon.Upload size={18} className="cursor-pointer" />
                    <Input
                      type="file"
                      id="header-file-upload"
                      className="file-upload cursor-pointer"
                      accept=".svg,image/svg+xml"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.type !== 'image/svg+xml' && !file.name.endsWith('.svg')) {
                            alert('Please upload an SVG file only.');
                            e.target.value = ''; // Reset input
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFieldValue('header', file);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                </div>
                {errors['header'] && <span className="error-message">{errors['header']}</span>}
              </FormGroup>
            </Col>
            <Col lg="4">
              <FormGroup>
                <Label htmlFor="favicon-file-upload">Favicon Icon</Label>
                <div className="file-upload-container cursor-pointer">
                  <Input
                    type="text"
                    className={`file-name cursor-pointer ${errors['favicon'] ? 'error-input' : ''}`}
                    placeholder={
                      values.favicon && values.favicon.name
                        ? values.favicon.name
                        : 'Choose a PNG file'
                    }
                    readOnly
                  />
                  <label className="upload-button cursor-pointer">
                    <Icon.Upload size={18} className="cursor-pointer" />
                    <Input
                      type="file"
                      id="favicon-file-upload"
                      className="file-upload cursor-pointer"
                      accept=".png,image/png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.type !== 'image/png' && !file.name.endsWith('.png')) {
                            alert('Please upload a PNG file only.');
                            e.target.value = ''; // Reset file input
                            return;
                          }

                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFieldValue('favicon', file);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                </div>
                {errors['favicon'] && <span className="error-message">{errors['favicon']}</span>}
              </FormGroup>
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="timezoneForm" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditBrandingModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditBrandingModal;
